import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import FilestoreBrowser from "../FilestoreBrowser";
import { FilestoreFile, parseFilestoreFile } from "../../api/filestore";
import { ImportPropertiesPayload } from "../../api/properties";

interface ImportPropertiesModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (payload: ImportPropertiesPayload) => Promise<unknown>;
  isPending: boolean;
  /** How many properties the project already has (drives the replace hint). */
  existingCount: number;
}

interface FilePreview {
  polygons: number;
  skipped: number;
  fields: string[];
}

/** Count polygon features and collect attribute names for the name picker. */
function previewGeojson(geojson: unknown): FilePreview {
  const features: unknown[] = Array.isArray((geojson as { features?: unknown[] })?.features)
    ? ((geojson as { features: unknown[] }).features ?? [])
    : [];
  let polygons = 0;
  const fields = new Set<string>();
  features.forEach((raw) => {
    const f = raw as { geometry?: { type?: string }; properties?: Record<string, unknown> };
    const type = f?.geometry?.type;
    if (type === "Polygon" || type === "MultiPolygon") polygons += 1;
    Object.keys(f?.properties ?? {}).forEach((k) => fields.add(k));
  });
  return { polygons, skipped: features.length - polygons, fields: Array.from(fields).sort() };
}

/** Best guess at a name column: "name"-ish first, else the first text field. */
function guessNameField(fields: string[]): string {
  const lower = fields.map((f) => f.toLowerCase());
  for (const candidate of ["name", "prop_name", "property", "title", "label", "lot", "id"]) {
    const idx = lower.findIndex((f) => f === candidate || f.includes(candidate));
    if (idx >= 0) return fields[idx];
  }
  return "";
}

/**
 * "Add property boundaries from file": pick a filestore file (zipped
 * shapefile / KML / GeoJSON — same as the create wizard), preview how many
 * polygons it holds, choose which attribute names each property, and either
 * append to or replace the project's properties. The file is parsed
 * server-side; the client only previews.
 */
export default function ImportPropertiesModal({
  open,
  onClose,
  onImport,
  isPending,
  existingCount,
}: ImportPropertiesModalProps) {
  const [browserOpen, setBrowserOpen] = useState(false);
  const [file, setFile] = useState<FilestoreFile | null>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [nameField, setNameField] = useState("");
  const [replace, setReplace] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setNameField("");
    setReplace(false);
    setError(null);
  };

  const handleFile = async (picked: FilestoreFile) => {
    setFile(picked);
    setPreview(null);
    setError(null);
    setPreviewing(true);
    try {
      const geojson = await parseFilestoreFile(picked.id);
      const next = previewGeojson(geojson);
      setPreview(next);
      setNameField(guessNameField(next.fields));
      if (next.polygons === 0) {
        setError("That file has no polygon features — property boundaries must be polygons.");
      }
    } catch {
      setError("Couldn't read that file. Shapefiles must be zipped; KML and GeoJSON also work.");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setError(null);
    try {
      await onImport({ filestoreFileId: file.id, nameField: nameField || null, replace });
      reset();
      onClose();
    } catch (e) {
      const message = (e as { message?: string })?.message;
      setError(message || "Import failed. Please try again.");
    }
  };

  const canSubmit = !!file && !previewing && (preview?.polygons ?? 0) > 0 && !isPending;

  return (
    <>
      <FilestoreBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelectFile={(picked) => void handleFile(picked)}
        acceptedFileTypes=".zip,.kml,.geojson,.json"
      />
      <Dialog
        open={open}
        onClose={() => {
          reset();
          onClose();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add property boundaries from file</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <Typography variant="caption" color="text.secondary">
            Zipped shapefile (.zip), KML or GeoJSON. Polygon features become
            properties; other geometry types are skipped.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setBrowserOpen(true)}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            {file ? file.original_filename : "Browse my files…"}
          </Button>
          {previewing && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Reading file…</Typography>
            </Box>
          )}
          {preview && (
            <Typography variant="body2">
              {preview.polygons} polygon{preview.polygons === 1 ? "" : "s"} found
              {preview.skipped > 0 ? ` (${preview.skipped} non-polygon features skipped)` : ""}.
            </Typography>
          )}
          {preview && preview.fields.length > 0 && (
            <TextField
              select
              size="small"
              label="Property name from"
              value={nameField}
              onChange={(e) => setNameField(e.target.value)}
              helperText='Leave as "Numbered" to name them Property 1, 2, 3…'
            >
              <MenuItem value="">Numbered (Property 1, 2, 3…)</MenuItem>
              {preview.fields.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </TextField>
          )}
          {existingCount > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={replace}
                  onChange={(e) => setReplace(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Replace the {existingCount} existing propert{existingCount === 1 ? "y" : "ies"}
                </Typography>
              }
            />
          )}
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isPending ? <CircularProgress size={18} /> : replace ? "Replace properties" : "Add properties"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
